import {
  Accordion,
  Alert,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { semanticColor } from "@how/design-system";
import type { ModuleKey } from "@how/types";
import { z } from "zod";
import { AREAS, EXTRAS, LEVEL_LABEL, areaModule, levelSentences, levelsFor } from "./role-catalog";
import { describeTeamError, useSaveCustomRole, type CustomRole, type Level } from "./team-queries";

const nameSchema = z
  .string()
  .trim()
  .min(1, "Give the role a name.")
  .max(80, "A role's name is up to 80 characters.");

export interface RoleEditorModalProps {
  /** null to build a new role, a role to edit it; undefined when closed. */
  readonly role: CustomRole | null | undefined;
  /** Modules this church can use; an area outside them is shown apart, its level waiting. */
  readonly entitled: ReadonlySet<ModuleKey>;
  readonly onClose: () => void;
}

function areaIsOnPlan(areaKey: string, entitled: ReadonlySet<ModuleKey>): boolean {
  const module = areaModule(areaKey);
  return module === null || entitled.has(module);
}

/**
 * Build or change a custom role (ADR 0054): a name, a level per area, and the three sensitive extras, off unless
 * switched on. What each level allows is written under its control, so nobody grants by guesswork. Saving rewrites
 * the role for everyone who holds it.
 */
export function RoleEditorModal({ role, entitled, onClose }: RoleEditorModalProps) {
  const save = useSaveCustomRole();
  // The page remounts this dialog per role (its key), so the form starts from the role being opened.
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [levels, setLevels] = useState<Record<string, Level>>(() => ({ ...(role?.levels ?? {}) }));
  const [extras, setExtras] = useState<readonly string[]>(role?.extras ?? []);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameCheck = nameSchema.safeParse(name);
  const nameError = touched && !nameCheck.success ? nameCheck.error.issues[0]?.message : undefined;
  const anything = Object.values(levels).some((l) => l !== "none") || extras.length > 0;
  const onPlan = AREAS.filter((a) => areaIsOnPlan(a.key, entitled));
  const offPlan = AREAS.filter((a) => !areaIsOnPlan(a.key, entitled));

  const areaRow = (area: { key: string; label: string }, muted: boolean) => {
    const current = levels[area.key] ?? "none";
    const offered = levelsFor(area.key);
    // A level set before the registry narrowed stays visible, so opening a role never changes it silently.
    const options = offered.includes(current) ? offered : [...offered, current];
    const allows = levelSentences(area.key, current);
    return (
      <Stack key={area.key} gap={4} data-area={area.key}>
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text size="sm" fw={600} {...(muted ? { c: "dimmed" } : {})}>
            {area.label}
          </Text>
          <SegmentedControl
            size="xs"
            aria-label={`${area.label} level`}
            value={current}
            data={options.map((l) => ({ value: l, label: LEVEL_LABEL[l] }))}
            onChange={(value) => {
              setLevels((prev) => ({ ...prev, [area.key]: value as Level }));
            }}
          />
        </Group>
        <Text size="xs" c="dimmed">
          {current === "none" ? "No access." : `Can: ${allows.join("; ")}.`}
        </Text>
      </Stack>
    );
  };

  return (
    <Modal
      opened={role !== undefined}
      onClose={onClose}
      title={role === null ? "New role" : `Edit ${role?.name ?? "role"}`}
      radius="lg"
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          required
          placeholder="Kids check-in lead"
          value={name}
          error={nameError}
          onChange={(event) => {
            const value = event.currentTarget.value;
            setName(value);
          }}
          onBlur={() => {
            setTouched(true);
          }}
        />
        <Textarea
          label="Description"
          description="A sentence for whoever gives this role to someone later."
          autosize
          minRows={2}
          value={description}
          onChange={(event) => {
            const value = event.currentTarget.value;
            setDescription(value);
          }}
        />

        <Stack gap="sm">
          <Title order={3} size="h5">
            What it can do
          </Title>
          <Text size="xs" c="dimmed">
            Levels build on each other: Edit includes View, Manage includes Edit. Staff, roles,
            settings, exports, refunds, API keys and integrations are never part of a custom role.
          </Text>
          {onPlan.map((area) => areaRow(area, false))}
          {offPlan.length === 0 ? null : (
            <Accordion variant="contained" chevronPosition="left">
              <Accordion.Item value="off-plan">
                <Accordion.Control>
                  <Text size="sm" c="dimmed">
                    Not on your plan ({offPlan.map((a) => a.label).join(", ")})
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <Text size="xs" c="dimmed">
                      A level set here waits, and takes effect if the feature is switched on for
                      your church.
                    </Text>
                    {offPlan.map((area) => areaRow(area, true))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
        </Stack>

        <Stack gap="sm">
          <Title order={3} size="h5">
            Sensitive extras
          </Title>
          <Text size="xs" c="dimmed">
            Off unless you switch them on. No level includes them, so nobody sees these by accident.
          </Text>
          {EXTRAS.map((extra) => (
            <Switch
              key={extra.key}
              label={extra.label}
              description={extra.description}
              checked={extras.includes(extra.key)}
              onChange={(event) => {
                const on = event.currentTarget.checked;
                setExtras((prev) =>
                  on ? [...prev, extra.key] : prev.filter((k) => k !== extra.key),
                );
              }}
            />
          ))}
        </Stack>

        {role !== null && role !== undefined && role.holders > 0 ? (
          <Text size="sm" c="dimmed">
            Saving changes what{" "}
            {role.holders === 1 ? "the 1 person" : `all ${String(role.holders)} people`} holding
            this role can do, at once.
          </Text>
        ) : null}
        {error === null ? null : (
          <Alert color={semanticColor("danger")} variant="light" role="alert">
            {error}
          </Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() => {
              setTouched(true);
              if (!nameCheck.success) return;
              if (!anything) {
                setError("Give the role at least one area above None, or one of the extras.");
                return;
              }
              setError(null);
              save.mutate(
                {
                  roleId: role?.id ?? null,
                  name,
                  description,
                  levels,
                  extras,
                },
                {
                  onSuccess: () => {
                    notifications.show({
                      color: semanticColor("success"),
                      title: role === null ? "Role created" : "Role saved",
                      message:
                        role === null
                          ? `${name.trim()} is ready to give to someone on the Team members tab.`
                          : `Everyone holding ${name.trim()} has the new access now.`,
                    });
                    onClose();
                  },
                  onError: (e) => {
                    setError(describeTeamError(e));
                  },
                },
              );
            }}
          >
            {role === null ? "Create role" : "Save role"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
